import React, { useCallback, useEffect, useState } from 'react';
import { Prompt } from '../../../components/Prompt/Prompt.component';
import { Examiner, ExaminerQuality } from '../../../models/taskGroup.types';
import { Typography } from '@material-ui/core';
import { useStyles } from './EnableExaminer.styles';
import { enableExaminer } from '../../../services/exams/assessmentSession/assessments.service';
import { getExaminerQuality } from '../../../services/evaluation/evaluation.service';
import { AxiosResponse } from 'axios';
import { BlockadeTypeId } from '../../../components/ExaminersStructure/ExaminersStructure.types';
import { getLockTypeText } from '../Helpers';
import { useLayoutContext } from '../../../Context/Layout.context';

export const EnableExaminer: React.FC<{
  examiner?: Examiner;
  examinerId?: string;
  examinerPermissionId: string;
  isQualified?: boolean;
  onSuccess: () => void;
  onFailure: () => void;
  isExaminingTeamChairmen: boolean;
  lockType: BlockadeTypeId;
  isControlProcessActive?: boolean;
  assessmentSessionId: string | undefined;
  isVerifier?: boolean;
}> = (props) => {
  const [openSuccessPrompt, setOpenSuccessPrompt] = useState<boolean>(false);
  const [openFailurePrompt, setOpenFailurePrompt] = useState<boolean>(false);
  const [openConfirmationPrompt, setOpenConfirmationPrompt] = useState<boolean>(true);
  const [examinerQuality, setExaminerQuality] = useState<ExaminerQuality | null>(null);

  const classes = useStyles();

  const { showLoader } = useLayoutContext();

  const enableSelectedExaminer = useCallback(() => {
    showLoader(true);
    setOpenConfirmationPrompt(false);
    enableExaminer(
      props.examiner ? props.examiner.examinerPermission[0]?.examinerPermissionId : props.examinerPermissionId,
    )
      .then(
        () => {
          setOpenSuccessPrompt(true);
        },
        () => {
          setOpenFailurePrompt(true);
        },
      )
      .finally(() => {
        showLoader(false);
      });
  }, [props.examiner, props.examinerPermissionId]);

  const getPromptSubTexts = (isSuccess?: boolean) => {
    return [
      {
        text: true ? props.examiner && props.examiner.firstName + ' ' + props.examiner && props.examiner.lastName : '',
      },
      {
        text: isSuccess ? 'zakończone sukcesem.' : 'nie powiodło się, spróbuj ponownie później.',
        isBold: true,
      },
    ];
  };

  useEffect(() => {
    if (
      (!props.isExaminingTeamChairmen &&
        props.examiner &&
        props.isQualified &&
        props.assessmentSessionId !== undefined) ||
      (props.isQualified && props.assessmentSessionId !== undefined)
    ) {
      showLoader(true);
      getExaminerQuality(props.assessmentSessionId, props.examinerId ?? '')
        .then((result: AxiosResponse<ExaminerQuality>) => {
          if (result.data) {
            setExaminerQuality(result.data);
          }
        })
        .finally(() => {
          showLoader(false);
        });
    }
  }, [props.assessmentSessionId, props.examiner, props.isExaminingTeamChairmen, props.isQualified]);

  function getSubTextForExaminer(): string {
    return props.lockType === BlockadeTypeId.Automatic && !props.examiner?.isQualified
      ? `Zdjęcie blokady automatycznie zakwalifikuje ${
          props.isVerifier ? 'Egzaminatora-Weryfikatora' : 'Egzaminatora'
        } do procesu oceniania`
      : `${
          props.isVerifier
            ? 'Odblokowanie Egzaminatora-Weryfikatora wznowi możliwość odesłania prac'
            : 'Odblokowanie Egzaminatora wznowi możliwość pobierania prac'
        }`;
  }

  return (
    <>
      <Prompt
        type="confirmation"
        isBold={true}
        open={openConfirmationPrompt}
        mainText={
          !props.isExaminingTeamChairmen
            ? `Czy na pewno chcesz odblokować ${props.isVerifier ? 'Egzaminatora-Weryfikatora' : 'Egzaminatora'}?`
            : 'Czy na pewno chcesz odblokować PZE?'
        }
        subTexts={[
          {
            text: !props.isExaminingTeamChairmen
              ? getSubTextForExaminer()
              : props.lockType === BlockadeTypeId.Automatic
              ? 'Zdjęcie blokady automatycznie zakwalifikuje PZE do procesu oceniania'
              : 'Odblokowanie PZE wznowi możliwość pobierania prac.',
          },
        ]}
        subIconText={`Blokada: ${getLockTypeText(props.lockType)}`}
        onConfirm={enableSelectedExaminer}
        onCancel={props.onFailure}
        mainActionButtonText="TAK"
      >
        <>
          {props.isControlProcessActive && examinerQuality !== null && (
            <div className={classes.listContainer}>
              <Typography
                style={{
                  fontWeight: 800,
                  color: '#3C419A',
                  padding: '10px',
                  textAlign: 'center',
                }}
              >
                {!props.isExaminingTeamChairmen
                  ? 'Aktualna wartość liczników błędych ocen egzaminatora:'
                  : 'Aktualna wartość liczników błędych ocen PZE:'}
              </Typography>
              <ul className={classes.list}>
                <li className={classes.listItem}>
                  Całkowita liczba ocen poza tolerancją:{' '}
                  <b>
                    {examinerQuality.examinerTotalOutOfTolerance ?? 0}/{examinerQuality.totalLimitOutOfTolerance ?? 0}
                  </b>
                </li>
                <li className={classes.listItem}>
                  Sekwencyjna liczba ocen poza tolerancją:{' '}
                  <b>
                    {examinerQuality.examinerSequentialOutOfTolerance ?? 0}/
                    {examinerQuality.sequentialLimitOutOfTolerance ?? 0}
                  </b>
                </li>
                <li className={classes.listItem}>
                  Całkowita liczba ocen w zakresie tolerancji:{' '}
                  <b>
                    {examinerQuality.examinerTotalWithinTolerance ?? 0}/
                    {examinerQuality.examinerSequentialWithinTolerance ?? 0}
                  </b>
                </li>
                <li className={classes.listItem}>
                  Sekwencyjna liczba ocen w zakresie tolerancji:{' '}
                  <b>
                    {examinerQuality.examinerSequentialWithinTolerance ?? 0}/
                    {examinerQuality.sequentialLimitWithinTolerance ?? 0}
                  </b>
                </li>
              </ul>
            </div>
          )}
        </>
      </Prompt>

      <Prompt
        type="error"
        isBold={true}
        open={openFailurePrompt}
        mainText={
          !props.isExaminingTeamChairmen
            ? `Odblokowanie ${props.isVerifier ? 'Egzaminatora-Weryfikatora' : 'Egzaminatora'}`
            : 'Odblokowanie PZE'
        }
        subTexts={getPromptSubTexts(false)}
        onConfirm={props.onFailure}
        mainActionButtonText="POWRÓT DO LISTY"
      />
      <Prompt
        type="success"
        isBold={true}
        open={openSuccessPrompt}
        mainText={
          !props.isExaminingTeamChairmen
            ? `Odblokowanie ${props.isVerifier ? 'Egzaminatora-Weryfikatora' : 'Egzaminatora'}`
            : 'Odblokowanie PZE'
        }
        subTexts={getPromptSubTexts(true)}
        onConfirm={props.onSuccess}
        mainActionButtonText="POWRÓT DO LISTY"
      />
    </>
  );
};

export default EnableExaminer;
